import { SlotEvent, SystemSlotEvent } from '@mbrtargeting/metatag-shared-types/metatag-core';
import { getViewportDimensions } from '@mbrtargeting/metatag-utils';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { selectCommonSettings, selectLayoutSettings, selectPositionSettings } from '../../../loader/essentials/config-selector.js';
import { triggerEvent, triggerHTMLElementEvent } from '../../../loader/essentials/events.js';
import { log } from '../../../loader/essentials/logger.js';
import { measurePerformance } from '../../../utils/performance.js';
import { SlotEffects } from '../../effects/index.js';
import { StateMachine } from '../../state-machine.js';
import { Adslot } from './adslot.js';

export enum LifecycleState {
    /**
     * The slot is newly registered.
     */
    REGISTERED = 'REGISTERED',
    /**
     * The slot is empty and available for ads.
     */
    AVAILABLE = 'AVAILABLE',
    /**
     * The slot is empty and has no ad.
     */
    EMPTY = 'EMPTY',
    /**
     * An ad has been rendered and the slot is used by an ad.
     *
     * Triggers certain slot behavior:
     *   - visibility measurement
     *   - ad label
     *   - etc
     */
    OCCUPIED = 'OCCUPIED',
    /**
     * slot will remain empty, but is used by another slot to display a special format.
     * Will avoid visibility measurement
     */
    BLOCKED = 'BLOCKED',
    /**
     * The slot was deregistered and is not available anymore.
     */
    DEREGISTERED = 'DEREGISTERED',
}

export enum LifecycleAction {
    /**
     * Constructs the adslot and its structure.
     */
    CONSTRUCT = 'CONSTRUCT',
    /**
     * Restore to initial state to reload an adslot.
     */
    RESET = 'RESET',
    /**
     * Put an ad on the page.
     */
    FILL = 'FILL',
    /**
     * Remove an ad from the page.
     */
    CLEAR = 'CLEAR',
    /**
     * slot will remain empty, but is used by another slot to display a special format, like a wallpaper using the sky slot
     */
    BLOCK_HIDE = 'BLOCK_HIDE',
    /**
     * slot will remain empty, but is used by another slot to display a special format, like a wallpaper using the sky slot
     */
    BLOCK_REMOVE = 'BLOCK_REMOVE',
    /**
     * deregister an adslot
     */
    DEREGISTER = 'DEREGISTER',
}

export class AdslotLifecycleState extends StateMachine<LifecycleState, LifecycleAction> {

    constructor(adslot: Adslot, configResolver: ConfigResolver) {
        super({
            logPrefix: `AdslotLifecycleState ${adslot.slotName.toUpperCase()}`,
            initialState: LifecycleState.REGISTERED,
            transitions: [
                { from: LifecycleState.REGISTERED, action: LifecycleAction.CONSTRUCT, to: LifecycleState.AVAILABLE },
                { from: LifecycleState.BLOCKED, action: LifecycleAction.RESET, to: LifecycleState.AVAILABLE },
                ...[LifecycleState.AVAILABLE, LifecycleState.EMPTY, LifecycleState.OCCUPIED].flatMap(from => [
                    { from, action: LifecycleAction.FILL, to: LifecycleState.OCCUPIED },
                    { from, action: LifecycleAction.CLEAR, to: LifecycleState.EMPTY },
                    { from, action: LifecycleAction.BLOCK_HIDE, to: LifecycleState.BLOCKED },
                    { from, action: LifecycleAction.BLOCK_REMOVE, to: LifecycleState.BLOCKED },
                    { from, action: LifecycleAction.RESET, to: LifecycleState.AVAILABLE },
                ]),
                ...Object.values(LifecycleState).flatMap(from => [
                    { from, action: LifecycleAction.DEREGISTER, to: LifecycleState.DEREGISTERED },
                ]),
            ],
            initialContext: {},
        });
        const slotSetupTime = measurePerformance(`Setup-${adslot.slotName.toUpperCase()}`).start();
        const slotAuctionTime = measurePerformance(`Auction-${adslot.slotName.toUpperCase()}`);
        const slotRenderTime = measurePerformance(`Rendering-${adslot.slotName.toUpperCase()}`);

        //used in most events as DetailMap concerning this slot
        const slotEventDetails = {
            passedObject: adslot,
            placement: adslot,
            slot: adslot.slotName,
            position: adslot.slotName,
            slotName: adslot.slotName,
        };

        // check if slot is limited by config
        this.onAction(LifecycleAction.CONSTRUCT, async () => {
            const { isBlocked } = configResolver?.get(selectPositionSettings(adslot.slotName)) || {};
            if (isBlocked) {
                slotSetupTime.end();
                throw new Error('Adslot blocked by config');
            }
        });

        // check 'isInViewportRange'
        this.onAction(LifecycleAction.CONSTRUCT, async () => {
            const mobileBreakpoint: number = parseInt(configResolver.get(selectCommonSettings()).mobileBreakpoint ?? '') || 0;
            const { minViewportWidth, maxViewportWidth, isMobileSlot } = configResolver.get(selectPositionSettings(adslot.slotName)) || {};
            const min: number = minViewportWidth ? parseInt(minViewportWidth) : (!isMobileSlot && mobileBreakpoint) ? mobileBreakpoint : 0;
            const max: number = maxViewportWidth ? parseInt(maxViewportWidth) : (isMobileSlot && mobileBreakpoint) ? mobileBreakpoint : 4e4;
            const { viewportWidth } = getViewportDimensions();
            const allowed: boolean = min < viewportWidth && viewportWidth < max;
            if (!allowed) {
                slotSetupTime.end();
                throw new Error('Adslot blocked by viewport');
            }
        });

        this.onEnter(LifecycleState.EMPTY, async () => {
            // hide ad label when slot is empty
            adslot.toggleEffect(SlotEffects.SHOW_AD_LABEL, false);
        });

        // when no ad was rendered hide slotcontainer if not using cls or explicitely allowed
        this.onState(LifecycleState.EMPTY, async () => {
            const { allowEmptySlotCollapse = false } = configResolver.get(selectLayoutSettings());
            const hideContainer = adslot.options.reserveSpace ? allowEmptySlotCollapse : true;
            triggerEvent(SystemSlotEvent.SDG_SLOT_EMPTY, slotEventDetails);
            triggerHTMLElementEvent(adslot.containerNode.parentElement, SlotEvent.SLOT_EMPTY, slotEventDetails);
            adslot.toggleEffect(SlotEffects.MARK_HULL_AS_RESPONDED, true);
            adslot.toggleEffect(SlotEffects.MARK_HULL_AS_UNFILLED, true); //legacy feature, see effect doc
            log.info(`${adslot.slotName.toUpperCase()}: is empty. Container ${(hideContainer) ? 'will be hidden' : 'will remain visible'}.`);
            adslot.toggleEffect(SlotEffects.REMOVE_SLOT_CONTAINER, hideContainer);
            slotRenderTime.end();
        });

        this.onState(LifecycleState.AVAILABLE, async () => {
            slotSetupTime.end();
            slotAuctionTime.start();
        });
        this.onLeave(LifecycleState.AVAILABLE, async () => {
            slotAuctionTime.end();
            slotRenderTime.start();
        });

        //remove container, even if CLS will happen. Blocker creative explicitly tells us so.
        this.onAction(LifecycleAction.BLOCK_REMOVE, async () => {
            log.info(`${adslot.slotName.toUpperCase()}: is blocked (BLOCK_REMOVE). Container & reserved space will be removed.`);
            adslot.toggleEffect(SlotEffects.REMOVE_SLOT_CONTAINER, true);
        });

        //either remove container completely or just hide it. Config tells us what to do. Remove might cause CLS, Hide might cause empty spaces.
        this.onAction(LifecycleAction.BLOCK_HIDE, async () => {
            const { allowEmptySlotCollapse = false } = configResolver.get(selectLayoutSettings());

            //should container be removed completely according to config?
            const removeContainerByConfig = adslot.options.reserveSpace ? allowEmptySlotCollapse : true;

            //should container be removed completely according to publisher?
            //todo option undocumented in pub doc, maybe rename to make better distinction between hide/remove
            const { hideWhenEmpty } = adslot.options

            //as long as no one tells us to, we stay save and avoid CLS. Empty spaces are more acceptable.
            const removeContainer = hideWhenEmpty || removeContainerByConfig

            adslot.toggleEffect(SlotEffects.MARK_HULL_AS_RESPONDED, true);
            log.info(`${adslot.slotName.toUpperCase()}: is blocked (BLOCK_HIDE). Container ${(removeContainer) ? '& reserved space will be removed' : 'will be invisible'}.`);
            adslot.toggleEffect((removeContainer) ? SlotEffects.REMOVE_SLOT_CONTAINER : SlotEffects.HIDE_SLOT_CONTAINER, true);
        });

        // add css class sdgResponded when slot contains an ad
        this.onEnter(LifecycleState.OCCUPIED, async () => {
            // show ad label if required as slot is occupied
            adslot.toggleEffect(SlotEffects.SHOW_AD_LABEL, adslot.options.showAdvertLabel);
            // add css class sdgResponded when slot contains an ad
            adslot.toggleEffect(SlotEffects.MARK_HULL_AS_RESPONDED, true);
        });

        this.onEnter(LifecycleState.BLOCKED, async () => {
            // hide ad label when slot is blocked
            adslot.toggleEffect(SlotEffects.SHOW_AD_LABEL, false);
            // add css class sdgResponded when slot contains a blocker
            adslot.toggleEffect(SlotEffects.MARK_HULL_AS_RESPONDED, true);
            slotRenderTime.end();
        });

        // remove css class sdgResponded when slot gets reset
        this.onAction(LifecycleAction.RESET, async () => {
            adslot.toggleEffect(SlotEffects.MARK_HULL_AS_RESPONDED, false);
            adslot.toggleEffect(SlotEffects.MARK_HULL_AS_UNFILLED, false); //legacy feature, see effect doc
        });

        // cleanup the adlibrary advertisement if present
        this.onAction(LifecycleAction.DEREGISTER, async () => {
            adslot.advert?.unloadAd();
        });

        // signal slot is deregistered after everything is deleted, but before we lose information about the slotContainer (to trigger event on)
        this.onState(LifecycleState.DEREGISTERED, async () => {
            triggerEvent(SystemSlotEvent.SDG_SLOT_DELETED, slotEventDetails);
            adslot.slotElementPromise.then((slotElement) => {
                triggerHTMLElementEvent(slotElement, SlotEvent.SLOT_DELETED, slotEventDetails);
            });
        });
    }
}
